import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Search, FileText, Printer } from 'lucide-react';
import { getDealClosures, getDailyLogs, getProjects, getCustomers } from '../api';
import { customerFullName } from '../lib/customerSearch';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CustomerReport = () => {
  const navigate = useNavigate();
  const [customerName, setCustomerName] = useState('');
  const [dateRange] = useState('999'); // All time - no filter
  const [reportData, setReportData] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [customerNumbers, setCustomerNumbers] = useState({});

  useEffect(() => {
    loadAllCustomers();
  }, []);

  const loadAllCustomers = async () => {
    try {
      // Load customers from Daily Logs (רישום יומי)
      const [dailyLogs, customers] = await Promise.all([getDailyLogs(), getCustomers().catch(() => [])]);
      const numbers = {};
      customers.forEach(c => { if (c.customerNumber) numbers[customerFullName(c).toLowerCase()] = c.customerNumber; });
      setCustomerNumbers(numbers);
      
      // Extract unique customer names from daily logs
      const customerNames = [...new Set(
        dailyLogs
          .map(log => log.customerName)
          .filter(name => name && name.trim() !== '')
      )];
      
      // Sort alphabetically
      customerNames.sort((a, b) => a.localeCompare(b, 'he'));
      
      setAllCustomers(customerNames);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const numberOf = (name) => customerNumbers[(name || '').trim().toLowerCase()];

  const handleSearch = async () => {
    const typed = customerName.trim().replace(/^#/, '');
    const byNumber = /^\d+$/.test(typed) ? Object.keys(customerNumbers).find(k => String(customerNumbers[k]) === typed) : null;
    const searchName = byNumber ? (allCustomers.find(n => n.trim().toLowerCase() === byNumber) || byNumber) : customerName;
    if (byNumber) setCustomerName(searchName);
    if (!customerName.trim()) {
      toast({
        title: 'שגיאה',
        description: 'נא להזין שם לקוח',
        variant: 'destructive'
      });
      return;
    }

    setIsSearching(true);
    try {
      // Load all data sources
      const [deals, dailyLogs, projects] = await Promise.all([
        getDealClosures(),
        getDailyLogs(),
        getProjects()
      ]);
      
      // Calculate date range
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      const monthsBack = parseInt(dateRange);
      
      // For "all time" (999), use a very early date
      const startDate = new Date();
      if (monthsBack === 999) {
        startDate.setFullYear(2000, 0, 1); // January 1, 2000
      } else {
        startDate.setMonth(startDate.getMonth() - monthsBack);
      }
      startDate.setHours(0, 0, 0, 0); // Start of the day

      // Filter and combine all records
      const filterByCustomerAndDate = (record) => {
        const recordDate = new Date(record.date);
        recordDate.setHours(0, 0, 0, 0);
        const matchesName = record.customerName?.toLowerCase().includes(searchName.toLowerCase());
        const matchesDate = recordDate >= startDate && recordDate <= today;
        return matchesName && matchesDate;
      };

      // Filter deals
      const filteredDeals = deals.filter(filterByCustomerAndDate).map(deal => ({
        ...deal,
        type: 'סגירת עסקה',
        displayName: deal.customerName
      }));

      // Filter daily logs
      const filteredDailyLogs = dailyLogs.filter(filterByCustomerAndDate).map(log => ({
        ...log,
        type: 'רישום יומי',
        displayName: log.customerName
      }));

      // Filter projects
      const filteredProjects = projects.filter(filterByCustomerAndDate).map(project => ({
        ...project,
        type: 'פרויקט',
        displayName: project.customerName
      }));

      // Combine all filtered records
      const allRecords = [...filteredDeals, ...filteredDailyLogs, ...filteredProjects];

      if (allRecords.length === 0) {
        toast({
          title: 'לא נמצאו תוצאות',
          description: `לא נמצאו רשומות עבור ${searchName} בטווח התאריכים שנבחר`,
          variant: 'destructive'
        });
        setReportData(null);
      } else {
        // Calculate totals
        let grandTotal = 0;
        let totalDebt = 0;
        const processedRecords = allRecords.map(record => {
          let recordTotal = 0;
          
          // Calculate total based on record type
          if (record.items && Array.isArray(record.items)) {
            // For deals and projects - sum of item totals
            recordTotal = record.items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
          } else if (record.workItems && Array.isArray(record.workItems)) {
            // For daily logs - sum of work items
            recordTotal = record.workItems.reduce((sum, item) => {
              const quantity = parseFloat(item.quantity) || 0;
              const pricePerUnit = parseFloat(item.transactionAmount) || 0;
              return sum + (quantity * pricePerUnit);
            }, 0);
          }
          
          grandTotal += recordTotal;
          
          // Calculate debt (if account not closed)
          if (!record.accountClosed) {
            const paidAmount = record.payments ? 
              record.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0) : 0;
            const debt = recordTotal - paidAmount;
            if (debt > 0) {
              totalDebt += debt;
            }
          }
          
          return { ...record, dealTotal: recordTotal };
        });

        // Sort by date (newest first)
        processedRecords.sort((a, b) => new Date(b.date) - new Date(a.date));

        setReportData({
          deals: processedRecords,
          customerName: searchName,
          dateRange: monthsBack,
          grandTotal,
          totalDebt
        });
        
        toast({
          title: 'הצלחה',
          description: `נמצאו ${allRecords.length} רשומות`
        });
      }
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: 'שגיאה',
        description: 'אירעה שגיאה בחיפוש',
        variant: 'destructive'
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handlePrint = () => {
    if (!reportData) {
      toast({
        title: 'שגיאה',
        description: 'אין נתונים להדפסה',
        variant: 'destructive'
      });
      return;
    }
    
    // Create print content
    const printContent = `
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>דוח ללקוח - ${reportData.customerName}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 30px; direction: rtl; background: white; }
          h1 { text-align: center; color: #1e40af; margin-bottom: 10px; font-size: 24px; }
          .date { text-align: center; color: #666; margin-bottom: 20px; font-size: 12px; }
          .info-box { background: #eff6ff; padding: 15px; border: 2px solid #bfdbfe; border-radius: 8px; margin-bottom: 20px; }
          .info-box p { margin: 5px 0; font-size: 14px; }
          .info-box span { font-weight: bold; }
          .record { border: 2px solid #d1d5db; border-radius: 8px; margin-bottom: 20px; overflow: hidden; page-break-inside: avoid; }
          .record-header { background: #f3f4f6; padding: 12px; border-bottom: 2px solid #d1d5db; display: flex; justify-content: space-between; }
          .record-header .title { font-weight: bold; font-size: 14px; }
          .record-header .total { font-weight: bold; color: #15803d; font-size: 16px; }
          .record-content { padding: 12px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #3b82f6; color: white; padding: 8px; border: 1px solid #333; font-size: 12px; }
          td { padding: 8px; border: 1px solid #333; text-align: center; font-size: 12px; }
          tr:nth-child(even) { background: #f9fafb; }
          .grand-total { background: #dcfce7; border: 2px solid #22c55e; border-radius: 8px; padding: 20px; margin-top: 20px; display: flex; justify-content: space-between; align-items: center; }
          .grand-total .label { font-size: 18px; font-weight: bold; }
          .grand-total .amount { font-size: 24px; font-weight: bold; color: #15803d; }
          .payments { margin-top: 10px; padding-top: 10px; border-top: 1px solid #e5e7eb; }
          .payment-item { display: flex; justify-content: space-between; background: #eff6ff; padding: 6px 10px; border-radius: 4px; margin: 4px 0; font-size: 12px; }
          @media print {
            body { padding: 15px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            @page { margin: 1cm; }
          }
        </style>
      </head>
      <body>
        <div style="text-align: center; border-bottom: 2px solid #3b82f6; padding-bottom: 15px; margin-bottom: 20px;">
          <h1 style="color: #1e40af; margin-bottom: 5px;">אימן שירותי מחשוב</h1>
          <p style="font-size: 13px; color: #374151; margin-bottom: 8px;">התקנת רשתות תקשורת | מכירה והתקנת מחשבים נייחים וניידים | מצלמות ומעגל סגור | אינטרקומים</p>
          <p style="font-size: 12px; color: #6b7280;">📧 aiman@aimanpc.com | 📞 052-576-7555</p>
        </div>
        <h2 style="text-align: center; color: #1e40af; margin-bottom: 10px; font-size: 20px;">דוח ללקוח</h2>
        <p class="date">תאריך הפקה: ${new Date().toLocaleDateString('he-IL')}</p>
        
        <div class="info-box">
          <p><span>לקוח:</span> ${reportData.customerName}</p>
          ${numberOf(reportData.customerName) ? `<p><span>מספר לקוח:</span> ${numberOf(reportData.customerName)}</p>` : ''}
          <p><span>תקופה:</span> ${
            reportData.dateRange === 1 ? 'חודש אחרון' :
            reportData.dateRange === 12 ? 'שנה אחרונה' :
            reportData.dateRange === 24 ? 'שנתיים אחרונות' :
            reportData.dateRange === 999 ? 'כל הזמן' :
            reportData.dateRange + ' חודשים אחרונים'
          }</p>
          <p><span>סה"כ רשומות:</span> ${reportData.deals.length}</p>
        </div>
        
        ${reportData.deals.map((deal, idx) => `
          <div class="record">
            <div class="record-header">
              <div>
                <div class="title">${deal.type} #${idx + 1}${deal.projectName ? ' - ' + deal.projectName : ''}</div>
                <div style="font-size: 12px; color: #666;">תאריך: ${new Date(deal.date).toLocaleDateString('he-IL')}</div>
              </div>
              <div class="total">סה"כ: ₪${(deal.dealTotal || 0).toFixed(2)}</div>
            </div>
            <div class="record-content">
              ${(deal.items && deal.items.length > 0) || (deal.workItems && deal.workItems.length > 0) ? `
                <table>
                  <thead>
                    <tr>
                      <th>חלק</th>
                      <th>כמות</th>
                      <th>מחיר ליחידה</th>
                      <th>מחיר ללקוח</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${(deal.items || []).map(item => {
                      const qty = parseFloat(item.quantity) || 0;
                      const price = parseFloat(item.sellingPrice || item.customerPrice) || 0;
                      return `
                        <tr>
                          <td>${item.partName || '-'}</td>
                          <td>${item.quantity || '0'}</td>
                          <td>₪${price.toFixed(2)}</td>
                          <td style="font-weight: bold;">₪${(item.customerPrice || qty * price).toFixed(2)}</td>
                        </tr>
                      `;
                    }).join('')}
                    ${(deal.workItems || []).map(item => {
                      const qty = parseFloat(item.quantity) || 0;
                      const price = parseFloat(item.transactionAmount) || 0;
                      return `
                        <tr>
                          <td>${item.partName || '-'}</td>
                          <td>${item.quantity || '0'}</td>
                          <td>₪${price.toFixed(2)}</td>
                          <td style="font-weight: bold;">₪${(qty * price).toFixed(2)}</td>
                        </tr>
                      `;
                    }).join('')}
                  </tbody>
                </table>
              ` : '<p style="text-align: center; color: #666;">אין פריטים</p>'}
              
              ${deal.payments && deal.payments.length > 0 ? `
                <div class="payments">
                  <div style="font-weight: bold; margin-bottom: 5px;">פרטי תשלום:</div>
                  ${deal.payments.map(p => `
                    <div class="payment-item">
                      <span>${p.method === 'cash' ? 'מזומן' : p.method === 'credit' ? 'אשראי' : p.method === 'check' ? 'שיק' : 'העברה'}</span>
                      <span style="font-weight: bold; color: #15803d;">₪${parseFloat(p.amount).toFixed(2)}</span>
                    </div>
                  `).join('')}
                </div>
              ` : ''}
            </div>
          </div>
        `).join('')}
        
        <div class="grand-total">
          <div class="label">סה"כ כולל לתקופה:</div>
          <div class="amount">₪${reportData.grandTotal.toFixed(2)}</div>
        </div>
        
        ${reportData.totalDebt > 0 ? `
          <div style="background: #fef2f2; border: 2px solid #ef4444; border-radius: 8px; padding: 20px; margin-top: 15px; display: flex; justify-content: space-between; align-items: center;">
            <div style="font-size: 18px; font-weight: bold; color: #dc2626;">סה"כ חוב:</div>
            <div style="font-size: 24px; font-weight: bold; color: #dc2626;">₪${reportData.totalDebt.toFixed(2)}</div>
          </div>
        ` : ''}
        
        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); }, 500);
          };
        </script>
      </body>
      </html>
    `;
    
    // Open in new window
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
    } else {
      toast({
        title: 'שגיאה',
        description: 'אנא אפשר חלונות קופצים בדפדפן',
        variant: 'destructive'
      });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `₪${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4" dir="rtl">
      <div className="container mx-auto max-w-6xl">
        <Card className="shadow-2xl border-0 mb-8 print:shadow-none">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg print:bg-white print:text-black">
            <CardTitle className="text-3xl font-bold">דווח ללקוח</CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            {/* Search Form */}
            <div className="space-y-6 mb-8 print:hidden">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="customerName" className="text-base font-semibold text-gray-700">
                    שם לקוח (מרישום יומי)
                  </Label>
                  <Select value={customerName} onValueChange={setCustomerName}>
                    <SelectTrigger className="text-base py-5 border-2">
                      <SelectValue placeholder="בחר לקוח מהרשימה" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]" dir="rtl">
                      {allCustomers.length > 0 ? (
                        allCustomers.map((name, index) => (
                          <SelectItem key={index} value={name} className="text-right">
                            {numberOf(name) ? `#${numberOf(name)} · ${name}` : name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-500">אין לקוחות ברישום יומי</div>
                      )}
                    </SelectContent>
                  </Select>
                  
                  {/* Manual Input Option */}
                  <div className="mt-2">
                    <Input
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="או הקלד שם או מספר לקוח"
                      data-testid="customer-report-manual-input"
                      className="text-sm py-3 border-2 focus:border-indigo-300"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={handleSearch}
                  disabled={isSearching}
                  className="flex-1 py-6 text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Search className="ml-2 w-5 h-5" />
                  {isSearching ? 'מחפש...' : 'חפש'}
                </Button>
                <Button
                  onClick={() => navigate('/reports')}
                  variant="outline"
                  className="px-8 py-6 text-lg border-2 hover:bg-gray-50 transition-colors"
                >
                  <ArrowRight className="ml-2 w-5 h-5" />
                  חזור
                </Button>
              </div>
            </div>

            {/* Report Display */}
            {reportData && (
              <div className="space-y-6">
                {/* Report Header */}
                <div className="border-b-2 border-gray-300 pb-4 print:border-black">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">דיווח ללקוח</h2>
                      <p className="text-lg text-gray-600 mt-2">לקוח: <span className="font-semibold">{reportData.customerName}</span></p>
                      {numberOf(reportData.customerName) && (
                        <p className="text-base text-gray-600" data-testid="customer-report-number">מספר לקוח: <span className="font-mono font-bold">{numberOf(reportData.customerName)}</span></p>
                      )}
                      <p className="text-base text-gray-600">
                        תקופה: {
                          reportData.dateRange === 1 ? 'חודש אחרון' :
                          reportData.dateRange === 12 ? 'שנה אחרונה' :
                          reportData.dateRange === 24 ? 'שנתיים אחרונות' :
                          reportData.dateRange === 999 ? 'כל הזמן' :
                          `${reportData.dateRange} חודשים אחרונים`
                        }
                      </p>
                      <p className="text-base text-gray-600">תאריך הדווח: {formatDate(new Date().toISOString())}</p>
                      <p className="text-sm text-blue-600 font-semibold mt-1">סה"כ {reportData.deals.length} רשומות</p>
                    </div>
                    <Button
                      onClick={handlePrint}
                      variant="outline"
                      className="print:hidden border-2 hover:bg-gray-50"
                    >
                      <Printer className="ml-2 w-5 h-5" />
                      הדפס
                    </Button>
                  </div>
                </div>

                {/* Records List */}
                <div className="space-y-6">
                  {reportData.deals.map((deal, dealIndex) => (
                    <Card key={deal.id} className="border-2 border-gray-200 print:break-inside-avoid">
                      <CardHeader className="bg-gray-50 border-b print:bg-white">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-lg font-semibold">
                              {deal.type} #{dealIndex + 1}
                              {deal.projectName && ` - ${deal.projectName}`}
                            </p>
                            <p className="text-sm text-gray-600">תאריך: {formatDate(deal.date)}</p>
                          </div>
                          <div className="text-left">
                            <p className="text-lg font-bold text-green-700">סה&quot;כ: {formatCurrency(deal.dealTotal)}</p>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-4">
                        {((deal.items && deal.items.length > 0) || (deal.workItems && deal.workItems.length > 0)) ? (
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b-2 border-gray-300">
                                  <th className="text-right py-2 font-semibold">חלק</th>
                                  <th className="text-right py-2 font-semibold">כמות</th>
                                  <th className="text-right py-2 font-semibold">מחיר ליחידה</th>
                                  <th className="text-right py-2 font-semibold">מחיר ללקוח</th>
                                </tr>
                              </thead>
                              <tbody>
                                {/* Display items (from deals/projects) */}
                                {deal.items && deal.items.map((item, itemIndex) => {
                                  const quantity = parseFloat(item.quantity) || 0;
                                  const pricePerUnit = parseFloat(item.sellingPrice || item.customerPrice) || 0;
                                  const totalPrice = quantity * pricePerUnit;
                                  
                                  return (
                                    <tr key={itemIndex} className="border-b border-gray-200">
                                      <td className="py-3">{item.partName || '-'}</td>
                                      <td className="py-3">{item.quantity || '0'}</td>
                                      <td className="py-3">{formatCurrency(pricePerUnit)}</td>
                                      <td className="py-3 font-semibold">{formatCurrency(item.customerPrice || totalPrice)}</td>
                                    </tr>
                                  );
                                })}
                                
                                {/* Display workItems (from daily logs) */}
                                {deal.workItems && deal.workItems.map((item, itemIndex) => {
                                  const quantity = parseFloat(item.quantity) || 0;
                                  const pricePerUnit = parseFloat(item.transactionAmount) || 0;
                                  const totalPrice = quantity * pricePerUnit;
                                  
                                  return (
                                    <tr key={`work-${itemIndex}`} className="border-b border-gray-200">
                                      <td className="py-3">{item.partName || '-'}</td>
                                      <td className="py-3">{item.quantity || '0'}</td>
                                      <td className="py-3">{formatCurrency(pricePerUnit)}</td>
                                      <td className="py-3 font-semibold">{formatCurrency(totalPrice)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-center py-4">אין פריטים ברשומה זו</p>
                        )}

                        {/* Payment Details */}
                        {deal.payments && deal.payments.length > 0 && (
                          <div className="mt-6 pt-4 border-t-2 border-gray-200">
                            <h4 className="text-base font-bold text-gray-800 mb-3">פרטי תשלום:</h4>
                            <div className="space-y-2">
                              {deal.payments.map((payment, paymentIndex) => (
                                <div key={paymentIndex} className="flex justify-between items-center bg-blue-50 p-3 rounded">
                                  <div className="flex gap-4 items-center">
                                    <span className="font-semibold text-gray-700">
                                      {payment.method === 'cash' && '💵 מזומן'}
                                      {payment.method === 'credit' && '💳 כרטיס אשראי'}
                                      {payment.method === 'check' && '📋 שיק'}
                                      {payment.method === 'transfer' && '🏦 העברה בנקאית'}
                                    </span>
                                    {payment.method === 'check' && payment.checkNumber && (
                                      <span className="text-sm text-gray-600">
                                        מס׳ {payment.checkNumber}
                                        {payment.checkDate && ` | ${formatDate(payment.checkDate)}`}
                                        {payment.bankName && ` | ${payment.bankName}`}
                                      </span>
                                    )}
                                    {payment.method === 'transfer' && payment.bankName && (
                                      <span className="text-sm text-gray-600">
                                        {payment.bankName}
                                        {payment.branch && ` - סניף ${payment.branch}`}
                                        {payment.accountNumber && ` - חשבון ${payment.accountNumber}`}
                                      </span>
                                    )}
                                    {payment.notes && (
                                      <span className="text-sm text-gray-500 italic">({payment.notes})</span>
                                    )}
                                  </div>
                                  <span className="font-bold text-green-700">{formatCurrency(payment.amount)}</span>
                                </div>
                              ))}
                              {deal.accountClosed && (
                                <div className="bg-green-100 border border-green-400 p-2 rounded text-center">
                                  <span className="text-green-800 font-semibold">✓ חשבון סגור</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Old Payment Field (for backward compatibility) */}
                        {deal.payment && !deal.payments?.length && (
                          <div className="mt-4 pt-3 border-t border-gray-200">
                            <p className="text-sm text-gray-600">
                              <span className="font-semibold">תשלום:</span> {deal.payment}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Grand Total */}
                <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 print:border-black">
                  <div className="flex justify-between items-center">
                    <p className="text-2xl font-bold text-gray-900">סה&quot;כ כולל לתקופה:</p>
                    <p className="text-3xl font-bold text-green-700">{formatCurrency(reportData.grandTotal)}</p>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">מספר עסקאות: {reportData.deals.length}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\:shadow-none, .print\:shadow-none * {
            visibility: visible;
          }
          .print\:shadow-none {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\:hidden {
            display: none !important;
          }
          .print\:bg-white {
            background-color: white !important;
          }
          .print\:text-black {
            color: black !important;
          }
          .print\:border-black {
            border-color: black !important;
          }
          .print\:break-inside-avoid {
            break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
};

export default CustomerReport;